<template>
  <div>
    <div class="flex flex-row">
      <div class="h-full w-1/3 px-12 p-5 flex flex-col">
        <img
          src="../../assets/shipo-itm-logo.png"
          class="mt-6 mb-12 w-1/4"
        >
        <h1 class="text-5xl font-semibold">
          Welcome To BLMS
        </h1>
        <p
          class="mt-2 text-xl text-gray-500"
          :class="[isError ? 'mb-5' : 'mb-12']"
        >
          Let's start your work now!
        </p>
        <div
          v-if="isError"
          class="flex flex-row p-5 mb-12 error-bg"
        >
          <img :src="require('../../assets/shipo-icon-error.svg')">
          <p class="ml-5 self-center text-danger">
            {{ errorMessage || 'Your username/password is not match or you are not registered yet, please contact your admin.' }}
          </p>
        </div>
        <div class="mb-4">
          <p class="mb-2 text-xl font-semibold">
            Username
          </p>
          <vs-input
            v-model="username"
            placeholder="Enter your username"
          />
        </div>
        <div class="mt-4 mb-24">
          <p class="mb-2 text-xl font-semibold">
            Password
          </p>
          <vs-input
            icon-after
            icon-no-border
            v-model="password"
            placeholder="Enter your password"
            :icon="isPasswordShown ? 'visibility_off' : 'visibility'"
            :type="isPasswordShown ? 'text' : 'password'"
            @icon-click="isPasswordShown = !isPasswordShown"
          />
        </div>
        <vs-button
          color="primary"
          class="px-2 py-6 font-semibold text-xl"
          @click="login"
        >
          <span class="uppercase">
            Login
          </span>
        </vs-button>
        <vs-progress
          indeterminate
          v-show="isLoggingIn"
          color="secondary"
          class="mt-2"
        />
        <vs-button
          color="secondary"
          class="px-2 py-6 mt-6 font-semibold text-xl"
          @click="loginWithAzure"
        >
          <div class="flex flex-row items-center gap-3">
            <img src="../../assets/shipo-icon-azure.svg">
            <span class="uppercase">
              Login With Azure
            </span>
          </div>
        </vs-button>
      </div>
      <div
        class="min-h-screen w-2/3 img-bg"
      />
      <vs-popup
        class="authenticate-loading-container"
        title="Authenticating"
        :button-close-hidden="true"
        :active.sync="isLoggingInWithAzure"
      >
        <div class="w-full flex flex-col items-center gap-5">
          <div
            id="authenticate-loading"
            class="vs-con-loading__container w-64 h-20"
          />
          <span>
            Authenticating your account to Azure
          </span>
          <span class="font-semibold">
            Please wait
          </span>
        </div>
      </vs-popup>
      <otp-pop-up
        :shown.sync="otpPopUpShown"
        :otp-code-model.sync="otpCode"
        :is-verify-ready.sync="verifyOtp"
      >
        <template #pop-up-content>
          <div class="flex flex-col items-center gap-6">
            <img
              src="../../assets/shipo-icon-mfa-lock.svg"
              style="width: 10rem"
            >
            <span class="text-base text-center">
              For added security, you’ll need to verify your identity. <br> We’ve sent a verification code to:
            </span>
            <span class="text-xl font-semibold">
              {{ otpSentTo }}
            </span>
            <span class="text-base font-medium">
              Please enter receive code below
            </span>
          </div>
        </template>
        <template #resend-new-code>
          <span
            v-if="!newOtpCodeRequested"
            class="text-base text-primary font-semibold cursor-pointer"
            @click="onResendOtpCode"
          >
            Resend a new code
          </span>
          <span
            v-else
            class="text-base"
          >
            You need to wait
            <span class="font-semibold">{{ newOtpCodeCountDown }} </span>
            before you sent another code
          </span>
        </template>
      </otp-pop-up>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import OtpPopUp from '@/components/otpPopUp'
import {
  AZURE_ERROR_MULTIPLE_SIGNED_IDENTITIES,
  AZURE_ERROR_NO_ACCOUNT_SIGNED_DETECTED,
  PAGE_TITLE
} from '@/fixtures/Constants'
import { isUserCanAccessFeature } from '@/utils/permissionUtils'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} Login`
  },
  components: {
    OtpPopUp
  },
  data() {
    return {
      username: null,
      password: null,
      isPasswordShown: false,
      isLoggingIn: false,
      isError: false,
      isLoggingInWithAzure: false,
      isSilentLoginAttempted: false,
      isSilentLoginAvailable: false,
      otpCode: null,
      verifyOtp: 0, // set to 0 and triggered by Date.now() to trigger changes in watch
      otpPopUpShown: false,
      otpSentTo: null,
      newOtpCodeRequested: false,
      newOtpCodeRequestedCount: 0,
      newOtpCodeCountDown: null,
      errorMessage: ''
    }
  },
  computed: {
    ...mapGetters({
      accessToken: 'OidcAuth/accessToken',
      oidcError: 'OidcAuth/oidcError',
      isLoading: 'OidcAuth/isLoading',
      silentSigninAvailable: 'OidcAuth/silentSigninAvailable'
    })
  },
  async mounted() {
    const isAzureMultipleAccount = sessionStorage.getItem('azure_multiple_account')
    if (isAzureMultipleAccount && isAzureMultipleAccount === 'true') {
      this.isSilentLoginAttempted = true
      sessionStorage.removeItem('azure_multiple_account')
    }

    await this.handleAzureCallback()
  },
  watch: {
    otpPopUpShown: {
      handler(val) {
        if(!val) {
          this.isLoggingIn = false
        }
      }
    },
    isLoading: {
      async handler(val) {
        this.isLoggingInWithAzure = val
      }
    },
    isLoggingInWithAzure: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.$vs.loading({
              color: 'secondary',
              container: '#authenticate-loading',
              scale: 1
            })
          })
        } else {
          this.$vs.loading.close('#authenticate-loading > .con-vs-loading')
        }
      }
    },
    oidcError: {
      handler(val) {
        if (!val) return;

        const errorDescription =
          typeof val === 'object' ? val?.error_description : val

        const isLoginRequired = errorDescription?.includes(AZURE_ERROR_NO_ACCOUNT_SIGNED_DETECTED)

        if (isLoginRequired) {
          this.isSilentLoginAvailable = false
          this.showWarningNotification(this, 'No Azure active session detected')
        } else {
          this.showFailedNotification(this, 'Failed to login with Azure')
        }
      }
    },
    silentSigninAvailable: {
      handler(val) {
        this.isSilentLoginAvailable = val
      }
    },
    newOtpCodeCountDown: {
      handler(val) {
        if (val === 0 || !val) {
          this.newOtpCodeRequested = false
          this.newOtpCodeCountDown = null
        } else if (!!val && val > 0) {
          setTimeout(() => {
            this.newOtpCodeCountDown--
          }, 1000)
        }
      }
    },
    verifyOtp: {
      handler(val) {
        if (val !== 0) {
          this.verifyOtpCode()
        }
      }
    }
  },
  methods: {
    login() {
      const validate = !this.isEmpty(this.username) && !this.isEmpty(this.password)
      if (!validate) {
        this.showFailedNotification(this, 'Username or Password cannot be empty', 'Error')
        return
      }

      this.isError = false
      this.isLoggingIn = true

      this.sendOtpCode()
    },
    async handleAzureCallback() {
      const hash = this.$route.hash
      const hasAccessToken = hash?.includes('access_token')
      const hasState = hash?.includes('state')

      const query = this.$route.query
      const postLogout = query?.post_logout_redirect || null

      const noAzureSessionAvailable = this.isSilentLoginAttempted && !this.isSilentLoginAvailable

      if (!!hasAccessToken && !!hasState && noAzureSessionAvailable ) {
        try {
          return this.azureSigninRedirectCallback()
        } catch (err) {
          this.isError = true
        }
      } else if (!!postLogout) {
        return await this.$router.push({ name: 'Login', replace: true })
      } else {
        try {
          await this.azureSigninSilentCallback()
          await this.azureSigninSilent()
          const token = new URLSearchParams(hash.substring(1)).get('access_token')
          if(token || this.accessToken) await this.onSucceedAzureLogin(token || this.accessToken)
        } catch (err) {
          this.isSilentLoginAvailable = false
        } finally {
          this.isSilentLoginAttempted = true
        }
      }
    },
    azureSigninRedirectCallback() {
      this.$store.dispatch('OidcAuth/signinRedirectCallback')
    },
    async azureSigninSilent() {
      try {
        await this.$store.dispatch('OidcAuth/signinSilent')
      } catch (err) {
        const { error_description } = err || null
        if (error_description && error_description?.includes(AZURE_ERROR_MULTIPLE_SIGNED_IDENTITIES)) {
          sessionStorage.setItem('azure_multiple_account', 'true')
          return this.$oidc.userManager.signinRedirect({
            prompt: 'select_account'
          })
        }
      }
    },
    async azureSigninSilentCallback() {
      await this.$store.dispatch('OidcAuth/signinSilentCallback')
    },
    async loginWithAzure() {
      this.isError = false
      try {
        await this.$store.dispatch('OidcAuth/signin')
      } catch (e) {
        this.isError = true
      }
    },
    async onSucceedAzureLogin(accessToken) {
      this.isError = false
      this.isLoggingInWithAzure = true
      this.$store.dispatch('Auth/loginWithAzure', { access_token: accessToken })
        .then(() => {
          const redirect = this.$route.query.redirect;
          const firstRoute = isUserCanAccessFeature()
          this.$router.push(redirect ? redirect : {name: firstRoute.firstRouteThatHaveAccess})
        })
        .catch(() => {
          this.isError = true
        })
        .finally(() => {
          this.isLoggingInWithAzure = false
        })
    },
    sendOtpCode() {
      this.$store.dispatch('Auth/sendOtpCode', { username: this.username, password: this.password })
        .then((response) => {
          if (response?.is_otp_enabled === false && response?.access_token) {
            return this.$store.dispatch('Auth/loginDirect', response)
              .then(() => {
                this.isLoggingIn = false
                const redirect = this.$route.query.redirect;
                const firstRoute = isUserCanAccessFeature()
                this.$router.push(redirect ? redirect : {name: firstRoute.firstRouteThatHaveAccess})
              })
              .catch((error) => {
                this.isLoggingIn = false
                this.isError = true
                this.errorMessage = error?.message || error?.error?.message
              })
          }
          this.otpPopUpShown = true
          this.otpSentTo = response?.email
          return null
        })
        .catch(({ error }) => {
          this.isError = true
          const defaultErrorMessage = error?.message && error?.message?.includes('These credentials do not match.')
          this.errorMessage = defaultErrorMessage ? 'Your username/password is not match or you are not registered yet, please contact your admin.' : error?.message
        })
    },
    verifyOtpCode() {
      this.$store.dispatch('Auth/verifyOtpCode', { username: this.username, code: this.otpCode })
        .then(() => {
          const redirect = this.$route.query.redirect;
          const firstRoute = isUserCanAccessFeature()
          this.$router.push(redirect ? redirect : {name: firstRoute.firstRouteThatHaveAccess})
        })
        .catch((error) => {
          this.showFailedNotification(this, error?.message)
        })
    },
    async onResendOtpCode() {
      this.newOtpCodeRequested = true
      this.newOtpCodeRequestedCount += 1
      this.newOtpCodeCountDown = this.newOtpCodeRequestedCount * 30

      this.$store.dispatch('Auth/resendOtpCode', { username: this.username })
        .then(() => {
          this.showSuccessNotification(this, 'Successfully send new otp code')
        })
        .catch(({ error }) => {
          this.showFailedNotification(this, error?.message)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-con-input-label {
  width: 100%;
}

/deep/.vs-input--input {
  background-color: #F1F1FD;
  border: 0 solid transparent !important;
  border-radius: 0.5rem;
  height: 3rem;
}

/deep/.vs-input--placeholder {
  top: auto;
}

/deep/.vs-input--icon {
  color: #493493;
}

/deep/.vs-input--icon.icon-after {
  border: none;
  right: 1.5rem !important;
  top: auto;
}

.error-bg {
  background-color: #FBE4E8;
  border-radius: 0.5rem;
}

.img-bg {
  background-image: url("../../assets/shipo-login-image.svg");
  background-size: cover;
  background-repeat: no-repeat;
}

.authenticate-loading-container {
  & /deep/.vs-popup {
    @apply p-3;
    width: fit-content;

    .vs-popup--header {
      @apply font-semibold;
      background: #fff;
    }

    .vs-popup--content {
      @apply flex items-center;
    }
  }
}
</style>
